import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useCreateOrganization } from "@/hooks/useCreateOrganization";
import { useUserOrganizations } from "@/hooks/useOrganization";
import AuthLayout from "@/components/auth/AuthLayout";
import AuthHeader from "@/components/auth/AuthHeader";
import SelectionCard from "@/components/auth/SelectionCard";
import Divider from "@/components/auth/Divider";
import GoogleButton from "@/components/auth/GoogleButton";

type AccountType = "brand" | "agency";

const SignUpStepTwo = () => {
    const navigate = useNavigate();
    const { signOut, signInWithGoogle } = useAuth();
    const { data: userOrganizations, isLoading } = useUserOrganizations();
    const createOrganization = useCreateOrganization();

    const [accountType, setAccountType] = useState<AccountType | null>(null);
    const [brandName, setBrandName] = useState("");

    const handleSignInClick = async () => {
        await signOut();
        navigate("/auth");
    };

    // Redirect if user already has an organization
    useEffect(() => {
        if (!isLoading && userOrganizations && userOrganizations.length > 0) {
            navigate("/", { replace: true });
        }
    }, [isLoading, userOrganizations, navigate]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!accountType) {
            toast({
                title: "Selection required",
                description: "Please select whether you are a Brand or Agency.",
                variant: "destructive",
            });
            return;
        }

        if (brandName.trim() === "") {
            toast({
                title: "Name required",
                description: "Please enter your organization name.",
                variant: "destructive",
            });
            return;
        }

        createOrganization.mutate(
            { name: brandName.trim(), organizationType: accountType },
            {
                onSuccess: () => {
                    navigate("/", { replace: true });
                },
            }
        );
    };

    const handleGoogleSignIn = async () => {
        const { error } = await signInWithGoogle();
        if (error) {
            toast({
                title: "Google sign in failed",
                description: error.message,
                variant: "destructive",
            });
        }
    };

    if (isLoading) {
        return <LoadingSpinner size="fullscreen" />;
    }

    return (
        <AuthLayout>
            <div className="flex flex-col min-h-[70vh]">
                <AuthHeader />

                <h2 className="text-[28px] font-extrabold text-center">
                    Create a free Opsero account
                </h2>

                <form onSubmit={handleSubmit} className="mt-8">
                    <p className="text-[22px] font-semibold mb-3">
                        What best describes you?
                    </p>

                    {/* Selection Cards */}
                    <div className="grid grid-cols-2 gap-4">
                        <SelectionCard
                            title="Brand"
                            selected={accountType === "brand"}
                            onClick={() => setAccountType("brand")}
                        />
                        <SelectionCard
                            title="Agency"
                            selected={accountType === "agency"}
                            onClick={() => setAccountType("agency")}
                        />
                    </div>

                    <div className="mb-6" />

                    {/* Organization Name */}
                    <Input
                        placeholder="Organization Name"
                        value={brandName}
                        onChange={(e) => setBrandName(e.target.value)}
                        maxLength={100}
                        required
                    />

                    <Button
                        type="submit"
                        disabled={createOrganization.isPending}
                        className="w-full mt-7 text-[20px] font-semibold bg-[#9333EA] leading-relaxed py-7"
                    >
                        {createOrganization.isPending ? (
                            <LoadingSpinner size="small" iconClassName="text-white" label="Creating..." />
                        ) : (
                            "Sign Up"
                        )}
                    </Button>

                    <div className="text-center mt-5 mb-8">
                        <span className="text-[20px] font-medium text-black">
                            Already have an account?{" "}
                        </span>
                        <button
                            type="button"
                            onClick={handleSignInClick}
                            className="text-[20px] text-[#9333EA] font-medium underline underline-offset-2 cursor-pointer"
                        >
                            Sign In
                        </button>
                    </div>

                    <Divider />

                    <div className="mt-8">
                        <GoogleButton
                            text="Continue with Google"
                            onClick={handleGoogleSignIn}
                        />
                    </div>

                    <div className="mt-auto pt-10">
                        <p className="text-[20px] font-medium text-[#616161] text-center">
                            By signing up, you agree to our{" "}
                            <span className="underline cursor-pointer underline-offset-2">
                                Terms
                            </span>{" "}
                            and{" "}
                            <span className="underline cursor-pointer underline-offset-2">
                                Privacy Policy
                            </span>.
                        </p>
                    </div>
                </form>
            </div>
        </AuthLayout>
    );
};

export default SignUpStepTwo;
